"use strict";

import jQuery from 'jquery';

/**
 * @classdesc
 * Hilfsklasse zum abstrahieren eines Formulars.
 * <br><strong>Achtung:</strong> Diese Klasse sollte nicht direkt instanziiert
 * werden (kann aber), da hier kein Renderingprozess stattfindet. Stattdessen
 * sollte eine Instanz über {@link gc.gis.util.GCFormRenderer.renderForm}
 * generiert werden.
 *
 * @class
 * @extents gc.gis.GCObservable
 * @memberof gc.gis.util
 * @author Anton Popov <a.popov@brain-scc.de>
 *
 * @param {Object} options Aufrufoptionen
 * @param {gc.gis.util.GCFormConfig} options.config
 * @param {jQuery} options.element Gerendertes jQuery-<code>form</code>-Element
 * @param {function} options.submit Submitfunktion
 */
gc.gis.util.GCForm = function( options )
{
	/**
	 * Kopie der Formular-Konfiguration.
	 *
	 * @type {gc.gis.util.GCFormConfig}
	 */
	this.config     = options.config;
	/**
	 * <code>form</code>-Elenent
	 *
	 * @type {jQuery}
	 */
	this.element    = options.element;
	/**
	 * Formularparameter, nachdem {@link gc.gis.util.GCForm._buildQueryParams}
	 * aufgerufen wurde.
	 *
	 * @type {Object}
	 */
	this.properties = {};

	this._initEvents();

	gc.gis.GCObservable.call( this );
};
gc.gis.util.GCForm.prototype = Object.create( gc.gis.GCObservable.prototype );

/**
 * Initialisiert die Submit-/Error-Callback für
 * <code>jQuery.on('submit')</code>.
 *
 * @protected
 * @method
 */
gc.gis.util.GCForm.prototype._initEvents = function()
{
	var self = this;

	this.element.on('submit', function( evt ) {
		evt.preventDefault();
		try
		{
			self.submit(evt);
			jQuery(this).blur();
		}
		catch( err )
		{
			if( typeof self.getConfig().error === 'function')
			{
				self.getConfig().error.call( this, self, err );
			}
			gc.gis.iface.log.error( self, err );
		}

		return false;
	});
};

/**
 * Gibt die Formular-Konfiguration zurück.
 *
 * @public
 * @method
 * @return {gc.gis.util.GCFormConfig}
 */
gc.gis.util.GCForm.prototype.getConfig = function()
{
	return this.config;
};

/**
 * Setzt die Control-Konfiguration für einen Schlüssel.
 * <br><strong>Achtung:</strong> das Formular wird nicht neu gerendert!
 *
 * @public
 * @method
 * @param {string} key Formularelementschlüssel.
 * @param {Object<string,*>} value Control-Konfiguration
 */
gc.gis.util.GCForm.prototype.set = function( key, value )
{
	this.properties[key] = value;
};

/**
 * @public
 * @method
 * @param {string} key Formularelementschlüssel.
 */
gc.gis.util.GCForm.prototype.get = function( key )
{
	if( this.properties[key] )
	{
		return this.properties[key];
	}
	throw new gc.gis.error.GCError('Form property not found: '+ key );
};

/**
 * Setzt den Wert einer Formularkomponente.
 *
 * @public
 * @method
 * @param {string} key Formularelementschlüssel.
 * @param {*} value Wert des Formularelements.
 * @throws {gc.gis.error.GCError} Wenn die Komponente nicht existiert.
 */
gc.gis.util.GCForm.prototype.setValue = function(key, value)
{
	const cfg = this.get(key);

	if (cfg)
	{
		if (cfg.getElement())
		{
			if (cfg.type === 'checkbox')
			{
				cfg.getElement().prop('checked', gc.gis.GCUtil.parseBool(value, false));
			}
			else
			{
				cfg.getElement().val(value);
			}
		}
		else if (cfg.options)	// untested
		{
			for (let i = 0; i < cfg.options.length; i++)
			{
				if (cfg.options[i].name === key)	// FIXME: geht doch garnicht! oder!?
				{
					cfg.options[i].getElement().attr('checked', value);
				}
			}
		}
		else
		{
			throw new gc.gis.error.GCError('Form element not found: '+ key);
		}
	}
	else	// sollte nicht vorkommen
	{
		throw new gc.gis.error.GCError('Form config not found: '+ key);
	}
};

/**
 * Gibt den Wert einer Formularkomponente zurück.
 *
 * @public
 * @method
 * @param {string} key Formularelementschlüssel.
 * @return {*} Wert des Formularelements.
 */
gc.gis.util.GCForm.prototype.getValue = function(key)
{
	var cfg = this.get( key );
	if( cfg )
	{
		if( cfg.getElement() )
		{
			if(cfg.type === 'checkbox')
			{
				return cfg.getElement().is(':checked');
			}

			return cfg.getElement().val();
		}
		else if( cfg.options )
		{
			var values = [];
			for( var i=0; i<cfg.options.length; i++ )
			{
				if( cfg.options[i].getElement().is(':checked') )
				{
					values.push( cfg.options[i].getElement().val() );
				}
			}
			if( cfg.type === 'radio' && values.length > 0 )
			{
				return values[0];
			}
			return values;
		}
		throw new gc.gis.error.GCError('Form element not found: '+ key );
	}
	throw new gc.gis.error.GCError('Form config not found: '+ key );
};

/**
 * Setzt die Bezeichnung eines Formularelements.
 *
 * @public
 * @method
 * @param {string} key Formularelementschlüssel.
 * @param {string} label Bezeichnung
 * @throws {gc.gis.error.GCError} Wenn die Komponente nicht existiert
 */
gc.gis.util.GCForm.prototype.setLabel = function(key, label)
{
	var cfg = this.get(key);

	if(!cfg) {
		throw new gc.gis.error.GCError('Form config not found: ' + key);
	}
	if (!cfg.getElement()) {
		throw new gc.gis.error.GCError('Form element not found: ' + key);
	}

	cfg.label = label;
	cfg.getElement().siblings('.control-label').text(label);
};

/**
 * Gibt das <code>&lt;form&gt;</code>-Element zurück, wenn kein Parameter
 * angegeben ist, oder das Element des übergebenen Formular-<code>name</code>'s.
 *
 * @public
 * @method
 * @param {string} [opt_key] Forumlarelementschlüssel
 * @return {jQuery}
 */
gc.gis.util.GCForm.prototype.getElement = function(opt_key)
{
	if(opt_key)
	{
		return this.get(opt_key).getElement();
	}

	return this.element;
};

/**
 * Baut die Query-Parameter zusammen und führt die Submit-Callback aus. Falls
 * definiert, wird vor dem Submit die Validate-Callback aufgerufen.
 *
 * @public
 * @method
 * @param {*} evt Event-Objekt
 * @throws {gc.gis.error.GCError} Wenn keine Submit-Callback konfiguriert wurde.
 */
gc.gis.util.GCForm.prototype.submit = function( evt )
{
    var params = this._buildQueryParams( this.getConfig() );
    if( typeof this.getConfig().validate === 'function')
    {
        this.getConfig().validate.call( this, this, params );
    }
    if( typeof this.getConfig().submit === 'function')
    {
        this.getConfig().submit.call( this, this, params );
    }
    else
    {
        throw new gc.gis.error.GCError('No submit function configured.');
    }
};

/**
 * Führt die Reset-Callback aus.
 *
 * @public
 * @method
 * @param {*} evt Event-Objekt
 * @throws {gc.gis.error.GCError} Wenn keine Reset-Funktion konfiguriert wurde.
 */
gc.gis.util.GCForm.prototype.reset = function( evt )
{
	if( typeof this.reset === 'function' )
	{
		this.reset.call( this, this, evt );
	}
	else
	{
		throw new gc.gis.error.GCError('No reset function configured.');
	}
};

/**
 * Deaktiviert ein einzelnes oder alle Steuerelemente des Formulars.
 *
 * @public
 * @method
 * @param {string} [opt_key] Forumlarelementschlüssel
 * @throws {gc.gis.error.GCError} Wenn die Komponente nicht existiert
 */
gc.gis.util.GCForm.prototype.disable = function(opt_key)
{
	if (opt_key)
	{
		var cfg = this.get(opt_key);

		if(!cfg) {
			throw new gc.gis.error.GCError('Form config not found: ' + opt_key);
		}
		if (!cfg.getElement()) {
			throw new gc.gis.error.GCError('Form element not found: ' + opt_key);
		}

		this._setDisabled(cfg, true);
	}
	else
	{
		this._setDisabled(this.getConfig(), true);
	}
};

/**
 * Aktiviert ein einzelnes oder alle Steuerelemente des Formulars.
 *
 * @public
 * @method
 * @param {string} [opt_key] Forumlarelementschlüssel
 * @throws {gc.gis.error.GCError} Wenn die Komponente nicht existiert
 */
gc.gis.util.GCForm.prototype.enable = function(opt_key)
{
	if (opt_key)
	{
		var cfg = this.get(opt_key);

		if(!cfg) {
			throw new gc.gis.error.GCError('Form config not found: ' + opt_key);
		}
		if (!cfg.getElement()) {
			throw new gc.gis.error.GCError('Form element not found: ' + opt_key);
		}

		this._setDisabled(cfg, false);
	}
	else
	{
		this._setDisabled(this.getConfig(), false);
	}
};

/**
 * Blendet ein einzelnes oder alle Steuerelemente des Formulars aus.
 *
 * @public
 * @method
 * @param {string} [opt_key] Forumlarelementschlüssel
 * @throws {gc.gis.error.GCError} Wenn die Komponente nicht existiert
 */
gc.gis.util.GCForm.prototype.hide = function(opt_key)
{
	if (opt_key)
	{
		var cfg = this.get(opt_key);
		if(!cfg)
		{
			throw new gc.gis.error.GCError('Form config not found: ' + opt_key);
		}
		if (!cfg.getElement())
		{
			throw new gc.gis.error.GCError('Form element not found: ' + opt_key);
		}

		this._showOrHide(cfg, true);
	}
	else
	{
		this._showOrHide(this.getConfig(), true);
	}
};

/**
 * Zeigt ein einzelnes oder alle Steuerelemente des Formulars an.
 *
 * @public
 * @method
 * @param {string} [opt_key] Forumlarelementschlüssel
 * @throws {gc.gis.error.GCError} Wenn die Komponente nicht existiert
 */
gc.gis.util.GCForm.prototype.show = function(opt_key)
{
	if (opt_key)
	{
		var cfg = this.get(opt_key);
		if(!cfg)
		{
			throw new gc.gis.error.GCError('Form config not found: ' + opt_key);
		}
		if (!cfg.getElement())
		{
			throw new gc.gis.error.GCError('Form element not found: ' + opt_key);
		}

		this._showOrHide(cfg);
	}
	else
	{
		this._showOrHide(this.getConfig());
	}
};

/**
 * Deaktiviert ein einzelnes oder alle Optionen einer Auswahlliste des Formulars.
 *
 * @public
 * @method
 * @param {string} key Forumlarelementschlüssel der Auswahlliste
 * @param {string} [opt_value] Wert des Eintrags der Auswahlliste, der deaktiviert werden soll
 * @throws {gc.gis.error.GCError} Wenn die Komponente nicht existiert
 */
gc.gis.util.GCForm.prototype.disableSelectOption = function(key, opt_value)
{
	this._setSelectOptionDisabled(key, true, opt_value);
};

/**
 * Aktiviert ein einzelnes oder alle Optionen einer Auswahlliste des Formulars.
 *
 * @public
 * @method
 * @param {string} key Forumlarelementschlüssel der Auswahlliste
 * @param {string} [opt_value] Wert des Eintrags der Auswahlliste, der deaktiviert werden soll
 * @throws {gc.gis.error.GCError} Wenn die Komponente nicht existiert
 */
gc.gis.util.GCForm.prototype.enableSelectOption = function(key, opt_value)
{
	this._setSelectOptionDisabled(key, false, opt_value);
};

/**
 * Durchläuft rekursiv die Formularkonfiguration, baut ein Parameterobjekt
 * zusammen und gibt es zurück.
 *
 * @protected
 * @method
 * @param {Object<string,*>} Formularkonfiguration
 * @return {Object<string,*>}
 */
gc.gis.util.GCForm.prototype._buildQueryParams = function( config )
{
	var params = {};

	for( var i=0; i<config.controls.length; i++ )
	{
		var ctrl = config.controls[i];
		if( ctrl.type === 'reset'
			|| ctrl.type === 'submit'
			|| ctrl.type === 'content')
		{
			continue;
		}

		var value = undefined;

		if( ctrl.type === 'hidden'
			|| ctrl.type === 'content'
			|| ctrl.type === 'textarea'
			|| ctrl.type === 'text'
			|| ctrl.type === 'number'
			|| ctrl.type === 'url')
		{
			if( ctrl.getElement() )
			{
				value = ctrl.getElement().val();
			}
		}
		else if( ctrl.type === 'select')
		{
			value = [];
			var options = ctrl.getElement().contents(':selected');
			for( var j=0; j<options.length; j++ )
			{
				options[j] = jQuery(options[j]);

				if( options[j].val() )
				{
					value.push( options[j].val() );
				}
				else if( options[j].data('__data') )
				{
					value.push( options[j].data('__data') );
				}
				else
				{
					value.push( options[j].text() );
				}
			}

			if (!ctrl.multiple) {
				value = value.length > 0 ? value[0] : '';
			}
		}
		else if( ctrl.type === 'checkbox')
		{
			value = ctrl.getElement().is(':checked');
		}
		else if( ctrl.type === 'radio')
		{
			for( var j=0; j<ctrl.options.length; j++ )
			{
				if( ctrl.options[j].getElement().is(':checked') )
				{
					value = ctrl.options[j].value;

					// Ggf. Sub-forms überprüfen
					if( ctrl.options[j].subForm )
					{
						var sub_params = this._buildQueryParams( ctrl.options[j].subForm );
						var keys = Object.keys( sub_params );
						for( var j=0; j<keys.length; j++ )
						{
							params[keys[j]] = sub_params[keys[j]];
						}
						continue;
					}

					break;
				}
			}
		}
		else if( ctrl.type === 'checkboxgroup')
		{
			value = [];

			for( var j=0; j<ctrl.options.length; j++ )
			{
				if( ctrl.options[j].getElement().is(':checked') )
				{
					value.push( ctrl.options[j].value );

					// Ggf. Sub-forms überprüfen
					if( ctrl.options[j].subForm )
					{
						var sub_params = this._buildQueryParams( ctrl.options[j].subForm );
						var keys = Object.keys( sub_params );
						for( var j=0; j<keys.length; j++ )
						{
							params[keys[j]] = sub_params[keys[j]];
						}
						continue;
					}
				}
			}
		}
		else if(ctrl.type === 'advanced' || ctrl.type === 'group')
		{
			var advanced_params = this._buildQueryParams( ctrl );
			var keys = Object.keys( advanced_params );
			for( var j=0; j<keys.length; j++ )
			{
				params[keys[j]] = advanced_params[keys[j]];
			}
			continue;
		}
		else if (ctrl.type === 'scale-selector')
		{
			value = ctrl.getWidget().getScale();
		}

		if( ctrl.name )
		{
			params[ctrl.name] = value;
		}

		// Ggf. Sub-forms überprüfen
		if( ctrl.subForm )
		{
			var sub_params = this._buildQueryParams( ctrl.subForm );
			var keys = Object.keys( sub_params );
			for( var j=0; j<keys.length; j++ )
			{
				params[keys[j]] = sub_params[keys[j]];
			}
			continue;
		}
	}

	return params;
};

/**
 * Setzt den Ladestatus des Formulars auf (in)aktiv. Ergänzt ein Element mit
 * einer Ladenachricht bzw. entfernt dieses. Setzt am Formular eine CSS-Klasse,
 * die entsprechend gestylt werden kann. Durchläuft alle Controls des Formulars
 * und <code>disabled</code> bzw. enabled sie.
 *
 * @public
 * @method
 * @param {boolean} loading Ladestatus aktiv?
 * @param {string} [opt_message='Lade ... '] Ladenachricht
 */
gc.gis.util.GCForm.prototype.setLoading = function(loading, opt_message)
{
	if(loading)
	{
		this.element.addClass('form-loading');
		var p_loading = jQuery('<p>', {
			'class': 'form-loading-indicator',
			html: '<span>'+ (opt_message ? opt_message : 'Lade ... ') + '</span>'
		});
		this.element.append(p_loading);
		p_loading.hide().show('blind');
	}
	else
	{
		this.element.removeClass('form-loading');
		this.element.find('.form-loading-indicator').hide('blind', {
			complete: function(evt) {
				jQuery(this).remove();
			}
		});
	}

	this._setDisabled(this.getConfig(), loading);
};

/**
 * Setzt das "disabled-Attribut" aller Steuerelement des Formulars.
 *
 * @param {Object} config
 * @param {boolean} disabled
 * @private
 */
gc.gis.util.GCForm.prototype._setDisabled = function(config, disabled)
{
	var controls = [],
		i;

	if(config.controls)
	{
		// gesamtes Formular de-/aktivieren
		controls = config.controls;
	}
	else if (config)
	{
		// einzelnes Element de-/aktivieren
		controls = [config];
	}

	for(i = 0; i < controls.length; i++)
	{
		if(controls[i].getElement())
		{
			if(controls[i].getElement() instanceof Array)
			{
				this._setDisabled(controls[i].getElement(), disabled);
			}
			else
			{
				controls[i].getElement().attr('disabled', disabled);
			}
		}
		else if(controls[i].options)
		{
			for(var j=0; j<controls[i].options.length; j++)
			{
				controls[i].options[j].getElement().attr('disabled', disabled);
			}
		}
	}
};

/**
 * Aktiviert ein einzelnes oder alle Optionen einer Auswahlliste des Formulars.
 *
 * @public
 * @method
 * @param {string} key Forumlarelementschlüssel der Auswahlliste
 * @param {boolean} disabled
 * @param {string} [opt_value] Wert des Eintrags der Auswahlliste, der deaktiviert werden soll
 * @throws {gc.gis.error.GCError} Wenn die Komponente nicht existiert
 */
gc.gis.util.GCForm.prototype._setSelectOptionDisabled = function(key, disabled, opt_value)
{
	var cfg = this.get(key);

	if(!cfg) {
		throw new gc.gis.error.GCError('Form config not found: ' + key);
	}
	if (!cfg.getElement()) {
		throw new gc.gis.error.GCError('Form element not found: ' + key);
	}
	if (cfg.type !== 'select') {
		throw new gc.gis.error.GCError('Type of form element is not a select: ' + key);
	}

	cfg.getElement().children('option').each(function () {
		var option = jQuery(this);
		if (opt_value === undefined || option.val() !== String(opt_value))
		{
			return;
		}

		option.attr('disabled', disabled === true);
		option.prop('disabled', disabled === true);
		disabled ? option.addClass('disabled') : option.removeClass('disabled');
	});
};

/**
 * Setzt das "disabled-Attribut" aller Steuerelement des Formulars.
 *
 * @param {Object} config
 * @param {boolean} hide
 * @private
 */
gc.gis.util.GCForm.prototype._showOrHide = function(config, hide)
{
	var controls = [],
		i, ii, form_grp;

	if(config.controls)
	{
		// gesamtes Formular de-/aktivieren
		controls = config.controls;
	}
	else if (config)
	{
		// einzelnes Element de-/aktivieren
		controls = [config];
	}

	for(i = 0, ii = controls.length; i < ii; i++)
	{
		if(controls[i].getElement())
		{
			form_grp = controls[i].getElement().parents("div .form-group");
			if (form_grp)
			{
				if (hide === true)
				{
					// ist ein Element im DOM enthalten aber aktuell nicht sichtbar, weil z.B. das gesamte Form nicht aktiv ist,
					// funktioniert die jQuery-Funktion .hide() mit einem duration Parameter nicht (??)
					if (form_grp.is(":visible"))
					{
						form_grp.hide(400);
					}
					else
					{
						form_grp.hide();
					}
				}
				else
				{
					form_grp.show(400);
				}
			}
		}
	}
};